import { useQuery } from '@tanstack/react-query';
import * as Combobox from '../../components/Combobox';
import { useRouteContext } from '@tanstack/react-router';
import { Destination } from '../../services/DestinationsApiClient';
import { YoutubeApiClient } from '../../services/YouTube';
import { Text } from '@streamelements/frontend-ui';
import { useFormContext } from 'react-hook-form';
import { FormValues } from '.';

type Props = {
  destinationMetadata: Destination<'youtube'>;
  output: Destination<'youtube'>['outputs'][number];
};

export function YouTubeStreamKeyField({ output, destinationMetadata }: Props) {
  const { authContextProvider, serviceEnvironment } = useRouteContext({
    from: '/_authenticated/settings/_layout/broadcast-settings/',
  });

  const form = useFormContext<FormValues>();
  const value = form.watch(`outputs.${output.id}.youtubeLiveStreamId`) ?? '';

  const { data: streamKeys = [] as never, isLoading } = useQuery({
    queryKey: ['youtube', 'stream-keys', destinationMetadata.accessToken],
    queryFn: async () => {
      const youtubeApiClient = new YoutubeApiClient({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata,
      });

      return youtubeApiClient.fetchStreamKeys();
    },
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
  });

  const streamKey = streamKeys.find(({ id }) => id === value) || {
    id: 'new',
    cdn: { resolution: 'variable', ingestionType: 'rtmp' },
    snippet: { title: 'Create new' },
  };

  const {
    cdn: { resolution, ingestionType = 'rtmp' } = { resolution: 'variable', ingestionType: 'rtmp' },
    snippet: { title = '' } = {},
  } = streamKey;

  return (
    <Combobox.ComboBoxRoot
      inputValue={
        isLoading
          ? 'Loading...'
          : streamKey
            ? `${title} (${ingestionType.toUpperCase()}, ${resolution})`
            : (value ?? '')
      }
      value={[value]}
      onValueChange={([next]) => {
        form.setValue(`outputs.${output.id}.youtubeLiveStreamId`, next, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }}
    >
      <Combobox.ComboBoxTrigger
        label="Stream key"
        name={`outputs.${output.id}.youtubeLiveStreamId` as const}
        inputProps={{ readOnly: true }}
      />
      <Combobox.ComboBoxContent>
        <Combobox.ComboBoxOption value="new">Create new</Combobox.ComboBoxOption>
        {streamKeys.map(({ id, cdn: { resolution = 'variable', ingestionType = 'rtmp' }, snippet: { title } }) => (
          <Combobox.ComboBoxOption
            key={id}
            value={id}
            label={`${title} (${resolution.toUpperCase()}, ${ingestionType})`}
            css={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Text.Body>{title}</Text.Body>
            <Text.Body css={{ color: '$textPrimary50', textTransform: 'capitalize' }} variant="caption">
              ({ingestionType.toUpperCase()}, {resolution})
            </Text.Body>
          </Combobox.ComboBoxOption>
        ))}
      </Combobox.ComboBoxContent>
    </Combobox.ComboBoxRoot>
  );
}
